import React, { Suspense, useRef, useEffect } from "react";
import { Canvas as CanvasBase, useLoader, useThree } from "@react-three/fiber";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Canvas = CanvasBase as any;
import { OrbitControls } from "@react-three/drei";
import { STLLoader } from "three-stdlib";
import { Box3, Vector3, Mesh, PerspectiveCamera } from "three";
import { Button } from "@/dentlab/src/components/ui/button";
import { Download } from "lucide-react";

interface StlPreviewProps {
	url: string;
	onDownload?: () => void;
}

const STLViewer: React.FC<{ stlUrl: string }> = ({ stlUrl }) => {
	const ref = useRef<Mesh>(null);
	const geometry = useLoader(STLLoader, stlUrl);
	const { camera } = useThree();

	useEffect(() => {
		if (geometry && ref.current) {
			const boundingBox = new Box3().setFromObject(ref.current);
			const center = new Vector3();
			boundingBox.getCenter(center);
			ref.current.position.sub(center);

			const perspectiveCamera = camera as PerspectiveCamera;

			if (perspectiveCamera.isPerspectiveCamera) {
				const size = boundingBox.getSize(new Vector3());
				const maxDim = Math.max(size.x, size.y, size.z);
				const fov = perspectiveCamera.fov * (Math.PI / 180);
				const cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));

				perspectiveCamera.position.z = cameraZ * 1.25;
			}
		}
	}, [geometry, camera]);

	// Cleanup geometry and materials on unmount
	useEffect(() => {
		return () => {
			if (geometry) {
				geometry.dispose();
			}
			if (ref.current) {
				// Dispose of the mesh's material
				const mesh = ref.current;
				if (mesh.material) {
					if (Array.isArray(mesh.material)) {
						mesh.material.forEach(material => material.dispose());
					} else {
						mesh.material.dispose();
					}
				}
			}
		};
	}, [geometry]);

	return (
		<mesh ref={ref} geometry={geometry} castShadow receiveShadow>
			<meshStandardMaterial
				color="orange"
				metalness={0.5}
				roughness={0.2}
			/>
		</mesh>
	);
};

export const StlFullViewer: React.FC<StlPreviewProps> = ({ url, onDownload }) => {
	return (
		<div className="relative h-full w-full">
			<Canvas
				style={{ height: "100%", width: "100%" }}
				shadows
				camera={{ position: [0, 0, 10], fov: 50 }}
			>
				<ambientLight intensity={0.4} />
				<directionalLight
					intensity={1.5}
					position={[5, 5, 5]}
					castShadow
					shadow-mapSize-width={2048}
					shadow-mapSize-height={2048}
				/>
				<pointLight position={[-10, -10, -10]} intensity={0.6} />
				<spotLight
					intensity={1}
					position={[10, 15, 10]}
					angle={0.3}
					castShadow
					shadow-mapSize-width={1024}
					shadow-mapSize-height={1024}
				/>
				<Suspense fallback={null}>
					<STLViewer stlUrl={url} />
				</Suspense>
				<OrbitControls />
			</Canvas>
			{onDownload && (
				<div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 bg-black/50 backdrop-blur-sm rounded-lg p-2 flex items-center gap-2">
					<Button
						variant="ghost"
						size="icon"
						onClick={onDownload}
						className="text-white hover:bg-white/20 h-8 w-8"
						aria-label="Datei herunterladen"
						data-testid="file-viewer-download-button"
						title="Herunterladen"
					>
						<Download className="h-4 w-4" />
					</Button>
				</div>
			)}
		</div>
	);
};
